/**
 * Stone wayside shrine (`hush.wayside_shrine.v1`) — a small ground piece for paths near the hideout.
 *
 * Frame 70×110, support at the bottom-center (35, 110). A flat footing slab carries a plinth of stacked stone
 * courses, a niche housing with an arched opening, and a tiny peaked slab roof with a cap stone. One candle stands
 * centered in the niche; `lit` adds its flame and a soft warm glow (light_core anchor at the flame). A pebble cluster
 * and a single flower sit as offerings on the footing, deliberately asymmetric. `moss` (0..1) sets how much moss
 * creeps over the roof and stones. Anti-face: the niche holds one centered light and no paired dark marks.
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies the stones.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface ShrineParams {
  readonly moss: number; // 0..1
  readonly lit: boolean;
}
export type ShrineRecipe = PieceRecipe<'hush.wayside_shrine.v1', ShrineParams>;

export const SHRINE_FRAME = { w: 70, h: 110 } as const;

export const HUSH_SHRINE: ShrineRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.wayside_shrine.stone.v1', generatorId: 'hush.wayside_shrine.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { moss: 0.45, lit: true },
};

export default HUSH_SHRINE;

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function params(p: unknown): ShrineParams {
  const r = p as Record<string, unknown>;
  const moss = r.moss;
  if (typeof moss !== 'number' || !(moss >= 0 && moss <= 1)) throw new Error('hush.wayside_shrine.v1: moss must be 0..1');
  if (typeof r.lit !== 'boolean') throw new Error('hush.wayside_shrine.v1: lit must be boolean');
  return { moss, lit: r.lit };
}

const W = SHRINE_FRAME.w, H = SHRINE_FRAME.h, CX = W / 2;

/** A slightly irregular rounded stone block between x0..x1, y0..y1; `j` jitters the corners. */
function stone(x0: number, y0: number, x1: number, y1: number, j: (k: string) => number): string {
  const r = Math.min(2.2, (y1 - y0) / 3);
  const a = { x: x0 + j('a'), y: y0 + j('b') * 0.5 }, b = { x: x1 + j('c'), y: y0 + j('d') * 0.5 };
  return `M${n(a.x + r)} ${n(a.y)}L${n(b.x - r)} ${n(b.y)}Q${n(b.x)} ${n(b.y)} ${n(b.x)} ${n(b.y + r)}`
    + `L${n(x1)} ${n(y1 - r)}Q${n(x1)} ${n(y1)} ${n(x1 - r)} ${n(y1)}L${n(x0 + r)} ${n(y1)}`
    + `Q${n(x0)} ${n(y1)} ${n(x0)} ${n(y1 - r)}L${n(a.x)} ${n(a.y + r)}Q${n(a.x)} ${n(a.y)} ${n(a.x + r)} ${n(a.y)}Z`;
}

export function buildShrine(recipe: ShrineRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.wayside_shrine.v1');
  checkScope(scope);
  const { moss, lit } = params(recipe.params);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;
  const jit = (key: string, amp: number) => (k: string) => (rnd(`${key}.${k}`) - 0.5) * amp;
  const stoneFill = (key: string) => {
    const t = rnd(`${key}.tone`);
    return t < 0.33 ? roles.stoneBase : t < 0.66 ? mix(roles.stoneBase, roles.stoneLight, 0.35) : mix(roles.stoneBase, roles.stoneShadow, 0.3);
  };
  const stoneEl = (key: string, x0: number, y0: number, x1: number, y1: number) =>
    el('path', { d: stone(x0, y0, x1, y1, jit(key, 1.2)), fill: stoneFill(key), stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round' })
    + el('path', { d: `M${n(x0 + 2)} ${n(y0 + 1.4)}L${n(x1 - 2.5)} ${n(y0 + 1.4)}`, stroke: roles.stoneLight, 'stroke-width': 0.9, opacity: 0.7, 'stroke-linecap': 'round' });

  // ---- footing: a broad flat slab that meets the ground at the support ----
  const footing = el('path', { d: stone(7, 101, 63, H - 0.4, jit('foot', 1)), fill: roles.stoneShadow, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' })
    + el('path', { d: `M10 ${n(102.6)}L60 ${n(102.6)}`, stroke: roles.stoneBase, 'stroke-width': 1.2, opacity: 0.8, 'stroke-linecap': 'round' });

  // ---- plinth: three courses of stacked stones, offset like masonry ----
  let plinth = '';
  const courses: Array<{ y0: number; y1: number; x0: number; x1: number; split: number[] }> = [
    { y0: 87, y1: 101, x0: 13, x1: 57, split: [0.38 + rnd('c0.s') * 0.1] },
    { y0: 75, y1: 87, x0: 15, x1: 55, split: [0.22 + rnd('c1.s0') * 0.08, 0.62 + rnd('c1.s1') * 0.1] },
    { y0: 63, y1: 75, x0: 14, x1: 56, split: [0.45 + rnd('c2.s') * 0.12] },
  ];
  courses.forEach((c, ci) => {
    const edges = [0, ...c.split, 1].map(t => c.x0 + (c.x1 - c.x0) * t);
    for (let i = 0; i < edges.length - 1; i++) plinth += stoneEl(`c${ci}.${i}`, edges[i] + (i ? 0.4 : 0), c.y0, edges[i + 1] - (i < edges.length - 2 ? 0.4 : 0), c.y1);
  });

  // ---- niche housing: a dressed block with one arched opening ----
  const hx0 = 17, hx1 = 53, hy0 = 30, hy1 = 63;
  let housing = el('path', { d: stone(hx0, hy0, hx1, hy1, jit('house', 0.8)), fill: roles.stoneBase, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
  housing += el('path', { d: `M${hx0 + 2} ${hy0 + 2}L${hx0 + 2} ${hy1 - 2}`, stroke: roles.stoneLight, 'stroke-width': 1.2, opacity: 0.7, 'stroke-linecap': 'round' });
  housing += el('path', { d: `M${hx1 - 2} ${hy0 + 3}L${hx1 - 2} ${hy1 - 2}`, stroke: roles.stoneShadow, 'stroke-width': 1.2, opacity: 0.6, 'stroke-linecap': 'round' });
  const ax0 = 26, ax1 = 44, ay1 = 59, archTop = 37, spring = 44;
  const arch = `M${ax0} ${ay1}L${ax0} ${spring}Q${ax0} ${archTop} ${CX} ${archTop}Q${ax1} ${archTop} ${ax1} ${spring}L${ax1} ${ay1}Z`;
  const nicheDark = lit ? mix(roles.woodShadow, roles.warmCore, 0.18) : mix(roles.stoneShadow, roles.outline, 0.45);
  let niche = el('path', { d: arch, fill: nicheDark, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
  niche += el('path', { d: `M${ax0 - 1} ${ay1}L${ax1 + 1} ${ay1}L${ax1 + 0.5} ${ay1 + 2}L${ax0 - 0.5} ${ay1 + 2}Z`, fill: roles.stoneLight, stroke: outline, 'stroke-width': 0.8 });

  // ---- candle: one centered stub; lit adds a flame and a soft glow ----
  const flame: Point = { x: CX, y: 47.5 };
  let candle = '';
  if (lit) {
    for (const [r, o] of [[9, 0.16], [6, 0.24], [3.6, 0.35]] as const) {
      candle += el('circle', { cx: flame.x, cy: flame.y + 2, r, fill: roles.warmCore, opacity: o });
    }
    candle += el('path', { d: arch, fill: roles.warmCore, opacity: 0.12 });
  }
  candle += el('path', { d: `M${CX - 2.4} ${ay1}L${CX - 2.4} 51.5Q${CX} 50.6 ${CX + 2.4} 51.5L${CX + 2.4} ${ay1}Z`, fill: roles.paperLight, stroke: outline, 'stroke-width': 0.8 });
  candle += el('path', { d: `M${CX} 51L${CX} 49.6`, stroke: outline, 'stroke-width': 0.6 });
  if (lit) {
    candle += el('path', { d: `M${CX} ${n(flame.y - 3.6)}Q${CX + 1.9} ${n(flame.y)} ${CX} ${n(flame.y + 1.9)}Q${CX - 1.9} ${n(flame.y)} ${CX} ${n(flame.y - 3.6)}Z`, fill: roles.warmCore, stroke: mix(roles.warmCore, roles.woodShadow, 0.4), 'stroke-width': 0.5 });
    candle += el('ellipse', { cx: CX, cy: flame.y + 0.4, rx: 0.7, ry: 1.2, fill: roles.paperLight, opacity: 0.9 });
  }

  // ---- roof: a tiny peaked slab with eaves and a cap stone ----
  const apex = { x: CX, y: 13 };
  const roofPts: Point[] = [{ x: 10, y: 31.5 }, { x: 13, y: 26.5 }, apex, { x: 57, y: 26.5 }, { x: 60, y: 31.5 }];
  let roof = el('path', { d: poly(roofPts), fill: mix(roles.stoneShadow, roles.stoneBase, 0.4), stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round' });
  roof += el('path', { d: `M14 27.4L${CX} 15.2`, stroke: roles.stoneLight, 'stroke-width': 1.3, opacity: 0.75, 'stroke-linecap': 'round' });
  roof += el('path', { d: `M10.5 30.5L59.5 30.5`, stroke: roles.stoneShadow, 'stroke-width': 1.1, opacity: 0.8 });
  roof += el('path', { d: stone(CX - 3.5, 8.5, CX + 3.5, 14.5, jit('cap', 0.6)), fill: roles.stoneBase, stroke: outline, 'stroke-width': 1 });

  // ---- moss: soft clumps on the roof slope and the stone tops ----
  let mossG = '';
  const nMoss = Math.round(moss * 9);
  const spots: Point[] = [
    { x: 18, y: 26 }, { x: 47, y: 22 }, { x: 27, y: 20 }, { x: 15, y: 88 }, { x: 54, y: 76 },
    { x: 41, y: 64 }, { x: 22, y: 76 }, { x: 52, y: 101 }, { x: 31, y: 88 },
  ];
  for (let i = 0; i < nMoss; i++) {
    const s = spots[i];
    const w = 3 + rnd(`moss.${i}.w`) * 4 * (0.6 + moss * 0.6);
    const x = s.x + (rnd(`moss.${i}.x`) - 0.5) * 3;
    mossG += el('path', {
      d: `M${n(x - w)} ${n(s.y + 1)}Q${n(x - w * 0.6)} ${n(s.y - 2.2)} ${n(x)} ${n(s.y - 1.6)}Q${n(x + w * 0.7)} ${n(s.y - 2.4)} ${n(x + w)} ${n(s.y + 1)}Q${n(x)} ${n(s.y + 2.4)} ${n(x - w)} ${n(s.y + 1)}Z`,
      fill: rnd(`moss.${i}.c`) < 0.5 ? roles.moss : roles.reedBase, stroke: roles.reedShadow, 'stroke-width': 0.6, opacity: 0.92,
    });
  }

  // ---- offerings: a pebble cluster on the left of the footing, one flower on the right ----
  let offerings = '';
  const pebbles = [{ x: 15, y: 99, r: 2.6 }, { x: 19.5, y: 99.6, r: 2 }, { x: 17, y: 96.4, r: 1.6 }];
  pebbles.forEach((p, i) => {
    offerings += el('ellipse', { cx: p.x + (rnd(`peb.${i}.x`) - 0.5), cy: p.y, rx: p.r * 1.2, ry: p.r * 0.85,
      fill: i === 1 ? roles.salt : mix(roles.stoneLight, roles.salt, 0.4), stroke: outline, 'stroke-width': 0.7 });
  });
  const fx = 51 + (rnd('flower.x') - 0.5) * 2, fy = 89 + rnd('flower.y') * 2;
  offerings += el('path', { d: `M${n(fx - 1)} 101Q${n(fx + 1.5)} ${n((fy + 101) / 2)} ${n(fx)} ${n(fy)}`, fill: 'none', stroke: roles.reedShadow, 'stroke-width': 1.1, 'stroke-linecap': 'round' });
  offerings += el('path', { d: `M${n(fx + 0.4)} ${n(fy + 6)}Q${n(fx + 4)} ${n(fy + 3.5)} ${n(fx + 4.5)} ${n(fy + 6.5)}Q${n(fx + 2)} ${n(fy + 7.5)} ${n(fx + 0.4)} ${n(fy + 6)}Z`, fill: roles.reedBase, stroke: roles.reedShadow, 'stroke-width': 0.5 });
  for (let i = 0; i < 5; i++) {
    const a = (Math.PI * 2 * i) / 5 + rnd('flower.rot');
    offerings += el('ellipse', { cx: n(fx + Math.cos(a) * 2), cy: n(fy + Math.sin(a) * 2), rx: 1.6, ry: 1.6, fill: roles.clothBlueLight, stroke: outline, 'stroke-width': 0.5 });
  }
  offerings += el('circle', { cx: fx, cy: fy, r: 1.1, fill: roles.warmCore, stroke: outline, 'stroke-width': 0.4 });

  const body = partGroup(parts, 'shrine-footing', footing)
    + partGroup(parts, 'shrine-plinth', plinth)
    + partGroup(parts, 'shrine-housing', housing)
    + partGroup(parts, 'shrine-niche', niche + candle)
    + partGroup(parts, 'shrine-roof', roof)
    + partGroup(parts, 'shrine-moss', mossG)
    + partGroup(parts, 'shrine-offerings', offerings);
  const support = { x: CX, y: H };
  return {
    frame: { ...SHRINE_FRAME },
    body,
    anchors: { support, light_core: flame, niche: { x: CX, y: 48 }, apex },
    parts,
  };
}
